import * as Sentry from '@sentry/react';
import axios from 'axios';

const SKETCHFAB_API_BASE_URL = 'https://api.sketchfab.com/v3';

const getSketchfabToken = () => {
  const token = process.env.REACT_APP_SKETCHFAB_TOKEN;
  if (!token) {
    throw new Error('Sketchfab token is not configured. Set REACT_APP_SKETCHFAB_TOKEN.');
  }
  return token;
};

const buildThumbnailUrl = thumbnails => {
  if (!thumbnails?.images?.length) {
    return null;
  }
  const preferredIndex = thumbnails.images.findIndex(item => item.width >= 640);
  if (preferredIndex >= 0) {
    return thumbnails.images[preferredIndex].url;
  }
  return thumbnails.images[thumbnails.images.length - 1].url;
};

// GET Obtiene las colecciones desde Sketchfab
export const getSketchfabCollections = async () => {
  const token = getSketchfabToken();
  const url = `${SKETCHFAB_API_BASE_URL}/me/collections`;

  const response = await axios
    .get(url, {
      headers: {
        Authorization: `token ${token}`,
      },
    })
    .then(res => res.data.results)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      throw err;
    });

  return response;
};

// GET Obtiene los detalles completos de una colección específica
export const getSketchfabCollectionByUid = async collectionUid => {
  if (!collectionUid) {
    throw new Error('Collection UID is required');
  }

  const token = getSketchfabToken();
  const url = `${SKETCHFAB_API_BASE_URL}/collections/${collectionUid}`;

  const response = await axios
    .get(url, {
      headers: {
        Authorization: `token ${token}`,
      },
    })
    .then(res => res.data)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      throw err;
    });

  return response;
};

// GET Obtiene todos los modelos de una colección específica
export const getModelsFromCollection = async collectionUid => {
  if (!collectionUid) {
    throw new Error('Collection UID is required');
  }

  const token = getSketchfabToken();
  const url = `${SKETCHFAB_API_BASE_URL}/me/search?type=models&collection=${collectionUid}`;

  const response = await axios
    .get(url, {
      headers: {
        Authorization: `token ${token}`,
      },
    })
    .then(res => {
      const formattedResults = res.data.results
        .map(result => ({
          uid: result.uid,
          name: result.name,
          description: result.description,
          uri: buildThumbnailUrl(result.thumbnails),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return formattedResults;
    })
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      throw err;
    });

  return response;
};

// GET Obtiene los detalles completos de un modelo específico
export const getSketchfabModelByUid = async uid => {
  if (!uid) {
    return null;
  }

  const token = getSketchfabToken();
  const url = `${SKETCHFAB_API_BASE_URL}/models/${uid}`;

  const response = await axios
    .get(url, {
      headers: {
        Authorization: `token ${token}`,
      },
    })
    .then(res => res.data)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      throw err;
    });

  return response;
};

// PATCH Actualiza los datos de una estructura anatómica (sketchfab)
export const updateSketchfabModel = async (uid, data) => {
  const token = getSketchfabToken();
  const url = `${SKETCHFAB_API_BASE_URL}/models/${uid}`;

  const response = await axios
    .patch(url, data, {
      headers: {
        Authorization: `token ${token}`,
      },
    })
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      throw err;
    });

  return response;
};
